import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmDeleteDialog } from 'components/ConfirmDeleteDialog';
import { useToast } from 'components/ui/toast';
import { apiErrorMessage } from 'utils/errorMessage';
import { getErrorMessage } from 'constants/errorCodes';
import { useCancelInterventionRequest } from 'hooks/useInterventionRequests';
import type { JiraWriteMeta } from 'api/interventionRequestsApi';
import AssignTechnicianDrawer from './AssignTechnicianDrawer';
import {
  assignmentLabel,
  assignmentState,
  technicianName,
  type UpcomingMission,
} from './upcomingAssignment';

/**
 * The assignment drawer and everything a click inside it sets off — the release confirmation, the
 * cancel mutation, and the two toasts that report what Jira did.
 *
 * On its own because **two** listings open it: « À qualifier », and « Tous les tickets », the only
 * door left once a send has taken the mission out of the other tab (C1).
 */

interface MissionAssignmentPanelProps {
  /** The mission the panel is open on, or null when it is closed. */
  mission: UpcomingMission | null;
  serviceDeskId: number;
  /**
   * The desk writes the qualification workflow into Jira (D17). **Off by default**, since the flag
   * ships OFF: nothing here then transitions anything, and no copy may claim it does.
   */
  hasInterventionQualification?: boolean;
  onClose: () => void;
  /** A Jira leg that failed on a write that succeeded, for a screen that shows it as a banner too. */
  onJiraWarning?: (message: string) => void;
}

const MissionAssignmentPanel = ({
  mission,
  serviceDeskId,
  hasInterventionQualification = false,
  onClose,
  onJiraWarning,
}: MissionAssignmentPanelProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const release = useCancelInterventionRequest();
  // A boolean, not a key: the release is always asked from the open drawer, so there is only ever
  // one mission it can name — and a second copy of it would be a second thing to keep in step.
  const [releasing, setReleasing] = useState(false);

  /**
   * @param jira - `meta.jira`, or null when the desk carries no workflow.
   * @param subject - The mission, and the technician where there is one, named in the copy.
   * @returns Whether the Jira leg failed.
   */
  const reportJira = (jira: JiraWriteMeta | null, subject: string): boolean => {
    if (!jira || jira.ok !== false) return false;
    // `said` and never `message`: only Jira's own answer says whether to fix an account or retry.
    const said = jira.said ? ` ${t('Jira says:')} ${jira.said}` : '';
    const message = `${getErrorMessage(t, jira.code)} ${subject}${said}`;
    onJiraWarning?.(message);
    toast.error(message);
    return true;
  };

  const confirmRelease = () => {
    if (!mission?.request) return;
    const { issueKey, request } = mission;
    release.mutate(
      { issueKey, serviceDeskId, requestId: request.id },
      {
        onSuccess: (result) => {
          setReleasing(false);
          const jira = result?.jira ?? null;
          if (reportJira(jira, issueKey)) return;
          // `transitioned`, not `ok`: the reverse leg answers `{ ok: true, transitioned: false }`
          // when the ticket was not on 10018, i.e. nothing moved and nothing came back.
          toast.success(
            jira?.ok === true && jira.transitioned === true
              ? t('The technician has been removed, and the mission is back in « À qualifier ».')
              : t('The technician has been removed from the mission.')
          );
        },
      }
    );
  };

  return (
    <>
      <AssignTechnicianDrawer
        mission={mission}
        serviceDeskId={serviceDeskId}
        hasInterventionQualification={hasInterventionQualification}
        onClose={onClose}
        onRelease={() => setReleasing(true)}
        onSent={(sent, name, jira) => {
          onClose();
          if (reportJira(jira, `${sent.issueKey} · ${name}`)) return;
          // A send no longer moves the ticket (D55): it stays in qualification until somebody
          // accepts, so there is nothing left to announce but the send itself. The « la mission
          // est passée en Intervention Programmée » half, and the guess that produced it, are gone.
          toast.success(`${t('The request has been sent.')} ${sent.issueKey} · ${name}`);
        }}
      />

      {/*
        Triggered from the drawer's assignment card, confirmed here: the mutation, its toasts and
        its Jira report stay in one place, and the drawer only names the mission it wants released.
        The mock-up's « Libérer le créneau » leaves with no confirmation and no undo.
      */}
      <ConfirmDeleteDialog
        open={releasing && mission !== null}
        tone="warning"
        title={t('Remove the technician from this mission?')}
        // Where the desk qualifies, removal also puts the ticket back on « En cours de
        // qualification » (PO, 09/09) — so the confirmation says where the ticket goes, not only
        // that a request is cancelled. With the flag off nothing transitions and it must not.
        description={
          hasInterventionQualification
            ? t('The request is cancelled, the ticket goes back to « En cours de qualification » and the mission returns to the « À qualifier » tab. Nothing is sent to anybody else — you choose the next one yourself.')
            : t('The request is cancelled and the mission goes back to having no technician. Nothing is sent to anybody else — you choose the next one yourself.')
        }
        details={
          mission?.request
            ? [
                { label: t('Mission'), value: mission.issueKey, emphasis: true },
                { label: t('Technician'), value: technicianName(mission.request), emphasis: true },
                { label: t('Status'), value: assignmentLabel(t, assignmentState(mission.request)) },
              ]
            : []
        }
        confirmLabel={t('Remove the technician')}
        loading={release.isPending}
        error={release.error ? apiErrorMessage(t, release.error) : null}
        onConfirm={confirmRelease}
        onClose={() => {
          setReleasing(false);
          release.reset();
        }}
      />
    </>
  );
};

export default MissionAssignmentPanel;
